import { signal } from '@preact/signals'
import type { ReadonlySignal } from '@preact/signals'

/** The most of one product a basket holds: the engine clamps an order line to 9 too. */
export const MAX_COUNT = 9

/** One product and how many, as `client.shops.order` takes it. */
export interface BasketLine {
  readonly item: string
  readonly count: number
}

/**
 * One shop's basket: item → count, in the order first added. The player's intent, not game
 * state: it's never saved, and the engine sees it only at checkout.
 */
export interface Basket {
  readonly counts: ReadonlySignal<ReadonlyMap<string, number>>
  count(item: string): number
  /** One more, up to `MAX_COUNT`. */
  add(item: string): void
  /** One less; the line goes at 0. */
  remove(item: string): void
  /** Sets the count, clamped to 0–`MAX_COUNT`; 0 drops the line. */
  set(item: string, count: number): void
  clear(): void
  lines(): readonly BasketLine[]
}

export function createBasket(): Basket {
  const counts = signal<ReadonlyMap<string, number>>(new Map())

  const set = (item: string, count: number): void => {
    const next = new Map(counts.value)
    const clamped = Math.min(MAX_COUNT, Math.max(0, Math.floor(count)))
    if (clamped === 0) next.delete(item)
    else next.set(item, clamped)
    counts.value = next
  }
  const count = (item: string): number => counts.value.get(item) ?? 0

  return {
    counts,
    count,
    add(item) {
      set(item, count(item) + 1)
    },
    remove(item) {
      set(item, count(item) - 1)
    },
    set,
    clear() {
      if (counts.value.size > 0) counts.value = new Map()
    },
    lines() {
      return [...counts.value].map(([item, n]) => ({ item, count: n }))
    },
  }
}
